import React from "react";
import Slider from "@mui/material/Slider";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { resolveCapacityUpgradePrompt } from "./capacityTierUtils";

const SLIDER_VALUE_BUBBLE_FILL = "#29489f";

const segmentPercent = (value, min, max) => {
  if (max <= min) return 100;
  return ((value - min) / (max - min)) * 100;
};

export default function CapacityTierSlider({
  capacityState,
  disabled = false,
  value,
  onChange,
  translationKeyPrefix = "event_create.step2.capacity",
  sliderAriaKey = "event_create.step2.slider_aria",
  variant = "default",
  showIncludesHostHint = false,
}) {
  const { t } = useTranslation("events");
  const navigate = useNavigate();
  const location = useLocation();

  const prompt = resolveCapacityUpgradePrompt(capacityState, value);
  const min = capacityState.participantsFloor;
  const max = capacityState.visualMaxParticipantsLimit;
  const basePercent = Math.max(
    0,
    Math.min(
      100,
      segmentPercent(capacityState.baseMaxParticipantsLimit, min, max)
    )
  );
  const freePercent = Math.max(
    basePercent,
    Math.min(
      100,
      segmentPercent(capacityState.freeMaxParticipantsLimit, min, max)
    )
  );
  const plusPercent = Math.max(
    freePercent,
    Math.min(
      100,
      segmentPercent(capacityState.plusMaxParticipantsLimit, min, max)
    )
  );
  const isHostPaidMode = capacityState.capacityMode === "host_paid";
  const isPremiumTier = capacityState.subscriptionTier === "premium";
  const isPlusTier = capacityState.subscriptionTier === "plus";

  const railBackground =
    max <= min
      ? "#d1d5db"
      : isHostPaidMode
        ? "linear-gradient(90deg, rgba(67,123,255,0.95) 0%, rgba(96,224,255,0.98) 100%)"
      : isPremiumTier
        ? "#f2f4f8"
      : isPlusTier
        ? `linear-gradient(90deg,
            #f2f4f8 0%,
            #f2f4f8 ${plusPercent}%,
            rgba(118,244,255,0.98) ${plusPercent}%,
            rgba(116,153,255,0.92) 100%)`
      : `linear-gradient(90deg,
          #f2f4f8 0%,
          #f2f4f8 ${basePercent}%,
          rgba(226,232,240,0.92) ${basePercent}%,
          rgba(226,232,240,0.92) ${freePercent}%,
          rgba(102,170,255,0.95) ${freePercent}%,
          rgba(102,170,255,0.95) ${plusPercent}%,
          rgba(118,244,255,0.98) ${plusPercent}%,
          rgba(116,153,255,0.92) 100%)`;

  const openSubscription = () => {
    if (!prompt) return;
    const params = new URLSearchParams({
      src: "event_capacity",
      feature: "event_capacity",
      tier: prompt.targetTier,
      from: `${location.pathname}${location.search}`,
    });
    navigate(`/menu/subscription?${params.toString()}`);
  };

  const handleSliderChange = (_event, nextValue) => {
    if (Array.isArray(nextValue)) return;
    onChange(Number(nextValue) || min);
  };

  if (variant === "additional") {
    const safeValue = Math.max(min, Math.min(max, Number(value) || min));
    const currentPercent = Math.max(0, Math.min(100, segmentPercent(safeValue, min, max)));
    const currentLimitValue = Math.min(
      max,
      Math.max(
        min,
        Number(capacityState.accessibleMaxParticipantsLimit) ||
          capacityState.baseMaxParticipantsLimit ||
          min
      )
    );
    const isFullyUnlocked = currentLimitValue >= max;
    const markerValues = isFullyUnlocked
      ? []
      : [
          currentLimitValue,
          capacityState.plusMaxParticipantsLimit,
          capacityState.premiumMaxParticipantsLimit,
        ].filter(
          (entry, index, list) =>
            Number.isFinite(entry) &&
            Number(entry) !== safeValue &&
            list.indexOf(entry) === index
        );
    const lockPositions = isFullyUnlocked
      ? []
      : [
          {
            key: "plus",
            start: currentLimitValue,
            end: capacityState.plusMaxParticipantsLimit,
          },
          {
            key: "premium",
            start: capacityState.plusMaxParticipantsLimit,
            end: capacityState.premiumMaxParticipantsLimit,
          },
        ].filter(
          ({ start, end }) =>
            Number.isFinite(start) &&
            Number.isFinite(end) &&
            end > start &&
            end > capacityState.accessibleMaxParticipantsLimit
        );

    return (
      <div className="mt-[14px]">
        <div className="relative h-[58px]">
          <div className="absolute left-[15px] right-[15px] top-[18px] h-1 rounded-[35px] bg-white/20" />
          <div
            className="absolute top-[18px] h-1 rounded-[35px] bg-white/80"
            style={{
              left: "15px",
              width: `max(0px, calc(15px + (100% - 60px) * ${currentPercent / 100}))`,
            }}
          />
          <div
            className="pointer-events-none absolute top-0 z-30 flex h-10 w-[60px] items-center justify-center rounded-[50px] border border-white/30 px-[19px] text-[20px] font-semibold leading-[22px] text-white shadow-[0_6px_18px_rgba(7,14,37,0.18)]"
            style={{
              left: `calc((100% - 60px) * ${currentPercent / 100})`,
              backgroundColor: SLIDER_VALUE_BUBBLE_FILL,
            }}
          >
            {safeValue}
          </div>
          <Slider
            aria-label={t(sliderAriaKey)}
            value={safeValue}
            onChange={handleSliderChange}
            step={1}
            min={min}
            max={max}
            disabled={disabled}
            track={false}
            sx={{
              position: "absolute",
              inset: "0 30px auto 30px",
              zIndex: 40,
              width: "calc(100% - 60px)",
              height: 40,
              padding: "0 !important",
              color: "transparent",
              "& .MuiSlider-rail": {
                opacity: 0,
              },
              "& .MuiSlider-track": {
                display: "none",
              },
              "& .MuiSlider-thumb": {
                backgroundColor: "transparent",
                boxShadow: "none",
                width: 60,
                height: 40,
                borderRadius: 50,
                cursor: disabled ? "default" : "grab",
              },
              "& .MuiSlider-thumb.Mui-active": {
                cursor: "grabbing",
              },
              "& .MuiSlider-thumb::before": {
                boxShadow: "none",
              },
              "& .MuiSlider-thumb.Mui-focusVisible, & .MuiSlider-thumb:hover": {
                boxShadow: "none",
              },
            }}
          />
          {markerValues.map((markerValue) => {
            const markerPercent = Math.max(0, Math.min(100, segmentPercent(markerValue, min, max)));
            const markerLeft =
              markerPercent >= 100
                ? "calc(100% - 15px)"
                : markerPercent <= 0
                  ? "15px"
                  : `calc(30px + (100% - 60px) * ${markerPercent / 100})`;
            return (
              <div
                key={`marker-${markerValue}`}
                className="pointer-events-none absolute top-[10px] z-[1] flex h-5 w-5 -translate-x-1/2 items-center justify-center rounded-full bg-white text-[10px] font-extrabold leading-4 text-[#3e6ffb]"
                style={{ left: markerLeft }}
              >
                {markerValue}
              </div>
            );
          })}
          {lockPositions.map(({ key, start, end }) => {
            const lockValue = start + (end - start) / 2;
            const lockPercent = Math.max(0, Math.min(100, segmentPercent(lockValue, min, max)));
            return (
              <div
                key={`lock-${key}`}
                className="pointer-events-none absolute top-[5px] z-[2] flex h-[30px] w-[30px] -translate-x-1/2 items-center justify-center rounded-full bg-[#8ea8ff] text-white shadow-[0_0_12px_rgba(255,255,255,0.12)]"
                style={{ left: `calc(30px + (100% - 60px) * ${lockPercent / 100})` }}
              >
                <img
                  src="/assets/events/capacity_lock.svg"
                  alt=""
                  className="h-5 w-5"
                  aria-hidden="true"
                />
              </div>
            );
          })}
        </div>

        <div className="mx-auto mt-[-6px] max-w-[272px] text-center text-[12px] font-normal leading-4 text-white/50">
          {prompt ? (
            <button
              type="button"
              onClick={openSubscription}
              className="block w-full text-center"
            >
              {t(`${translationKeyPrefix}.unlock_slots`, {
                defaultValue: "Unlock up to {{count}} slots with ",
                count: prompt.targetLimit,
              })}
              <span className="font-semibold italic underline underline-offset-2">
                {prompt.targetTier === "premium" ? "VG Premium" : "VG Plus"}
              </span>
            </button>
          ) : null}
          {showIncludesHostHint ? (
            <p className={prompt ? "mt-1" : ""}>
              {t(`${translationKeyPrefix}.includes_host_hint`, {
                defaultValue: "Selected capacity includes you",
              })}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mx-auto flex h-8 w-[95%] items-center">
        <Slider
          aria-label={t(sliderAriaKey)}
          value={value}
          onChange={handleSliderChange}
          step={1}
          min={min}
          max={max}
          disabled={disabled}
          track={false}
          sx={{
            height: 8,
            "& .MuiSlider-thumb": {
              backgroundColor: "#FFFFFF",
              boxShadow: "0 0 0 6px rgba(255,255,255,0.08)",
              width: 20,
              height: 20,
            },
            "& .MuiSlider-track": {
              display: "none",
            },
            "& .MuiSlider-rail": {
              background: railBackground,
              opacity: 1,
              borderRadius: "999px",
              boxShadow: "0 0 24px rgba(118, 244, 255, 0.18)",
            },
            "& .MuiSlider-thumb.Mui-disabled": {
              width: 18,
              height: 18,
            },
            "& .MuiSlider-rail.Mui-disabled": {
              opacity: 0.55,
            },
          }}
        />
      </div>

      {prompt ? (
        <div className="mt-4 rounded-2xl border border-[#5c9fff]/35 bg-[linear-gradient(180deg,rgba(18,24,40,0.96),rgba(17,25,46,0.92))] p-4 shadow-[0_18px_45px_rgba(0,0,0,0.24)]">
          <p className="text-sm leading-6 text-white/72">
            {prompt.targetTier === "plus"
              ? t(`${translationKeyPrefix}.plus_body`, {
                  defaultValue:
                    "Unlock Plus to create larger events with up to {{count}} participants.",
                  count: prompt.targetLimit,
                })
              : t(`${translationKeyPrefix}.premium_body`, {
                  defaultValue:
                    "Unlock Premium to create larger events with up to {{count}} participants.",
                  count: prompt.targetLimit,
                })}
          </p>
          <button
            type="button"
            onClick={openSubscription}
            className="mt-4 inline-flex items-center rounded-2xl border border-[#77b6ff]/55 bg-[linear-gradient(180deg,#4f8ef1_0%,#3d73dd_100%)] px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_28px_rgba(61,115,221,0.32)]"
          >
            {prompt.targetTier === "premium"
              ? t(`${translationKeyPrefix}.open_premium`, {
                  defaultValue: "Open Premium",
                })
              : t(`${translationKeyPrefix}.open_plus`, {
                  defaultValue: "Open Plus",
                })}
          </button>
        </div>
      ) : null}
    </div>
  );
}
